import { z } from "zod"

/**
 * Canonical LayoutName union. Owned by packages/model (a config-contract concern)
 * so it can be referenced without a dependency cycle; packages/layouts re-exports
 * it and owns the registry (US-013).
 *
 * - `default` — the delivery-app shell every tenant started on.
 * - `classic` — the traditional UK-takeaway shell: fixed photo backdrop, three
 *   stacked header bands, per-route rails. See packages/layouts/src/layouts/classic.
 * - `express` — the food-delivery app shell: a sitewide sidebar nav at the
 *   reading start, a persistent cart rail at the reading end, and a mobile
 *   bottom tab bar. See packages/layouts/src/layouts/express.
 * - `showcase` — the takeaway masthead shell built around a promo BANNER: a
 *   square photo slideshow with a caption strip and tile transitions on the
 *   home page, under a dark utility band, a photo masthead and a
 *   primary-coloured nav band. See packages/layouts/src/layouts/showcase.
 */
export const LAYOUT_NAMES = [
  "default",
  "classic",
  "express",
  "showcase",
] as const

export type LayoutName = (typeof LAYOUT_NAMES)[number]

export const DEFAULT_LAYOUT: LayoutName = "default"

/**
 * Validates a raw layout name from tenant config against the known union; anything
 * unknown/missing falls back to the default (never a broken page).
 */
export const layoutNameSchema = z.enum(LAYOUT_NAMES).catch(DEFAULT_LAYOUT)

/** Narrow an untrusted layout value to a valid LayoutName. */
export function resolveLayoutName(raw: unknown): LayoutName {
  return layoutNameSchema.parse(raw)
}
